import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import { addExtras, updateExtraByName } from 'apps/auth/actions/extraAction';
import { changeFormField } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { CalculatorAmountContainer, CalculatorFrameTop } from 'apps/auth/components/common/Calculator/styles';
import { CalculatorInputAmount } from 'apps/auth/components/common/Calculator/subcomponents/CalculatorInputAmount';
import { RECALCULATE } from 'apps/auth/constants/ExtraNames';
import { AMOUNT_FROM, AMOUNT_FROM_CCY, AMOUNT_TO, AMOUNT_TO_CCY } from 'apps/auth/constants/fieldsName';
import update from 'apps/auth/helpers/Immutability';
import { getActualMaxAmountFrom, timer } from 'apps/auth/helpers/utils';
import { useExtra } from 'apps/auth/hooks/selectors';
import { ApiService } from 'apps/auth/services/apiService';
import { FontSizeService } from 'apps/auth/services/fontSizeService';

const CalculatorPureComponent = (props) => {
    const timerId = useRef(null);
    const controllerRef = useRef(null);
    const [fontSize, setFontSize] = useState('');
    const [notValidateAmount, setNotValidateAmount] = useState(true);
    const fontSizeService = useMemo(() => new FontSizeService(), []);
    const { leftLimitAmount, canMakeDraftTransaction, recalculate, userCountry, toCountry, globalCountryTo, set_zero_amount, additionalAmlLimit } = useExtra();

    useEffect(() => {
        if (set_zero_amount) {
            updateAmountField(props.amountFrom, 0, false, '', '');
        }
        updateFontSize();
    }, []);

    const canCalculateCommission = (field) => props.isCanCalcCommission && field.value && !props.isShowSpinner && field.name === document.activeElement?.id;

    useEffect(() => {
        if (canCalculateCommission(props.amountFrom)) {
            timerId.current = timer(
                timerId.current,
                () => {
                    calcCommission(true);
                },
                500,
            );
        } else {
            updateFontSize();
            if (!props.amountFrom.value && props.amountTo.value) {
                updateAmountField(props.amountTo, 0, false, '', '');
            }
        }
    }, [props.amountFrom.value]);

    useEffect(() => {
        if (canCalculateCommission(props.amountTo)) {
            timerId.current = timer(
                timerId.current,
                () => {
                    calcCommission(false);
                },
                500,
            );
        } else {
            updateFontSize();
        }
    }, [props.amountTo.value]);

    useEffect(() => {
        if (recalculate === true) {
            calcCommission(true);
            props.updateExtraByName(RECALCULATE, false);
        }
    }, [recalculate]);

    useEffect(() => {
        if (props.amountFrom.value && props.amountTo.value && props.restrictionFrom && props.restrictionTo) {
            if (!set_zero_amount || (set_zero_amount && !notValidateAmount)) {
                let isShowToViewDetailsFromValue = validateMinMaxMultiplePocketAmount(
                    props.amountFrom,
                    props.amountFrom.value,
                    props.restrictionFrom.minAmount,
                    props.restrictionFrom.maxAmount,
                    props.restrictionFrom.multiple,
                    true,
                    props.currencyFrom.code,
                );
                if (!isShowToViewDetailsFromValue) {
                    validateMinMaxMultiplePocketAmount(
                        props.amountTo,
                        props.amountTo.value,
                        props.restrictionTo.minAmount,
                        props.restrictionTo.maxAmount,
                        props.restrictionTo.multiple,
                        false,
                        props.currencyTo.code,
                    );
                }
            }
        }
        setNotValidateAmount(false);
    }, [props.amountFromCcy, props.amountToCcy]);

    const calcCommission = (isFrom) => {
        props.setDisabledSubmitButton(true);
        const params = new URLSearchParams({ isFrom });
        props.typeOfTransfer && params.append('type', props.typeOfTransfer);
        const url = `${props.fetchCommissionUrl}?${params.toString()}`;
        ApiService.fetchWithoutSpinner(
            url,
            props.fields,
            (json) => {
                if (json.messages) {
                    props.updatePageMessages(json.messages);
                }
                if (json.extra) {
                    props.addExtras(json.extra).then(() => {
                        const commission = json.extra.commission;
                        if (commission) {
                            if (isFrom) {
                                validateAmountTo(commission.to);
                            } else {
                                validateAmountFrom(commission.from);
                            }
                        }
                    });
                }
            },
            controllerRef,
        );
    };

    const updateAmountField = (field, value, isError, errorCode, errorMessage) => {
        const newField = update(field, {
            $auto: {
                value: { $set: value },
                isError: { $set: isError },
                errorMessage: { $set: errorMessage },
                error: {
                    $auto: {
                        code: { $set: errorCode },
                        body: { $set: errorMessage },
                    },
                },
            },
        });
        return props.changeFormField(props.formName, newField);
    };

    const validateAmountFrom = (value) => {
        if (!props.restrictionFrom) return false;
        return validateMinMaxMultiplePocketAmount(
            props.amountFrom,
            value,
            props.restrictionFrom.minAmount,
            getActualMaxAmountFrom(userCountry, leftLimitAmount, canMakeDraftTransaction, props.restrictionFrom),
            props.restrictionFrom.multiple,
            true,
            props.currencyFrom.code,
        );
    };

    const validateAmountTo = (value) => {
        if (!props.restrictionTo) return false;
        return validateMinMaxMultiplePocketAmount(
            props.amountTo,
            value,
            props.restrictionTo.minAmount,
            props.restrictionTo.maxAmount,
            props.restrictionTo.multiple,
            false,
            props.currencyTo?.code,
        );
    };

    const validateMinMaxMultiplePocketAmount = (field, value, min, max, multiple, isFrom, currencyCode) => {
        let error = props.getValidationError(value, min, max, multiple, isFrom, currencyCode);
        updateAmountField(field, value, error.isError, error.error?.code, error.errorMessage);
        return error.isShowToViewDetails;
    };

    const updateFontSize = () => {
        setFontSize(fontSizeService.calc(props.amountFrom.value, props.amountTo.value));
    };
    const decreaseAmountFrom = (amountForChange) => {
        const transactionLimit = props.restrictionFrom.maxAmount;
        const isBonus = leftLimitAmount && props.isBonus;
        const isProfileLimits = leftLimitAmount && !amountForChange && !canMakeDraftTransaction;
        const isAdditionalLimit = additionalAmlLimit && props.amountFrom.value > additionalAmlLimit.left;

        let min = transactionLimit;
        if (amountForChange) {
            min = amountForChange;
        } else if (isProfileLimits || isBonus) {
            min = Math.min(leftLimitAmount, +transactionLimit);
        } else if (isAdditionalLimit) {
            min = additionalAmlLimit.left;
        }

        return updateAmountField(props.amountFrom, min, false);
    };
    const decreaseAmountToForMultipleError = () => {
        let currentValue = props.amountTo.value;
        let multiple = props.restrictionTo.multiple;
        let max = props.restrictionTo.maxAmount;
        let futureValue = max;

        if (currentValue <= max && multiple && currentValue % multiple !== 0) {
            // округляем в меньшую сторону
            futureValue = Math.floor(currentValue / multiple) * multiple;
        }
        updateAmountField(props.amountTo, futureValue, false);
    };

    return (
        <CalculatorFrameTop>
            <CalculatorAmountContainer>
                <CalculatorInputAmount
                    focus={true}
                    isGaFlow={props.isGaFlow}
                    isBonus={props.isBonus}
                    formName={props.formName}
                    amountField={props.amountFrom}
                    ccyField={props.amountFromCcy}
                    title={props.amountFromTitle}
                    restriction={props.restrictionFrom}
                    currencies={props.currenciesFrom}
                    customValidate={validateAmountFrom}
                    updateAmountField={updateAmountField}
                    customFontSize={fontSize}
                    currency={props.currencyFrom}
                    isSend={props.isSend}
                    isFrom={true}
                    decreaseAmountFrom={decreaseAmountFrom}
                    decreaseAmountButtonClick={decreaseAmountFrom}
                    onSelectCurrencyClick={props.onSelectCurrencyClick}
                    updateFontSize={updateFontSize}
                    isDouble
                    iconName={props.fromCountryFlagName}
                    getCurrencyIcon={props.getCurrencyIcon}
                    onIconRenderError={props.onIconRenderError}
                    setDisabledSubmitButton={props.setDisabledSubmitButton}
                />
                <CalculatorInputAmount
                    isGaFlow={props.isGaFlow}
                    isBonus={props.isBonus}
                    formName={props.formName}
                    amountField={props.amountTo}
                    ccyField={props.amountToCcy}
                    title={props.amountToTitle}
                    restriction={props.restrictionTo}
                    currencies={props.currenciesTo}
                    customValidate={validateAmountTo}
                    updateAmountField={updateAmountField}
                    customFontSize={fontSize}
                    currency={props.currencyTo}
                    isSend={props.isSend}
                    decreaseAmountButtonClick={decreaseAmountToForMultipleError}
                    decreaseAmountFrom={decreaseAmountFrom}
                    onSelectCurrencyClick={props.onSelectCurrencyClick}
                    displayArrows={!props.isSend}
                    updateFontSize={updateFontSize}
                    isDouble
                    iconName={toCountry?.id === 1001 ? globalCountryTo?.code : toCountry?.code}
                    getCurrencyIcon={props.getCurrencyIcon}
                    onIconRenderError={props.onIconRenderError}
                    setDisabledSubmitButton={props.setDisabledSubmitButton}
                />
            </CalculatorAmountContainer>
        </CalculatorFrameTop>
    );
};

CalculatorPureComponent.propTypes = {
    isShowSpinner: PropTypes.bool.isRequired,
    amountToTitle: PropTypes.string.isRequired,
    amountFromTitle: PropTypes.string.isRequired,
    commission: PropTypes.object,
    fields: PropTypes.object.isRequired,
    amountFrom: PropTypes.object.isRequired,
    amountTo: PropTypes.object.isRequired,
    restrictionFrom: PropTypes.object,
    restrictionTo: PropTypes.object,
    fetchCommissionUrl: PropTypes.string.isRequired,
    isSend: PropTypes.bool.isRequired,
    isRepeat: PropTypes.bool,
    isCanCalcCommission: PropTypes.bool,
    getValidationError: PropTypes.func.isRequired,
    onSelectCurrencyClick: PropTypes.func.isRequired,
    currencyTo: PropTypes.object,
    currencyFrom: PropTypes.object,
    currenciesFrom: PropTypes.array,
    currenciesTo: PropTypes.array,
    amountToCcy: PropTypes.object.isRequired,
    amountFromCcy: PropTypes.object.isRequired,
    isBonus: PropTypes.bool,
};

const mapStateToProps = (state, ownProps) => {
    return {
        isShowSpinner: state.app.isShowSpinner,
        amountToTitle: state.messages.pageMessages.amountTo,
        amountFromTitle: state.messages.pageMessages.amountFrom,
        commission: state.extra.commission,
        fields: state.form[ownProps.formName].fields,
        amountFrom: state.form[ownProps.formName].fields[AMOUNT_FROM],
        amountTo: state.form[ownProps.formName].fields[AMOUNT_TO],
        restrictionFrom: state.extra.commission?.restrictionFrom,
        restrictionTo: state.extra.commission?.restrictionTo,
        amountToCcy: state.form[ownProps.formName].fields[AMOUNT_TO_CCY],
        amountFromCcy: state.form[ownProps.formName].fields[AMOUNT_FROM_CCY],
    };
};

export const Calculator = connect(mapStateToProps, {
    changeFormField,
    updatePageMessages,
    addExtras,
    updateExtraByName,
})(CalculatorPureComponent);
